import { Button } from '@/components/ui/design-system';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { H4, P3 } from '@/components/ui/text';
import { useTeamSubscriptionData } from '@/hooks/api/team';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useState } from 'react';
import type { ContactView } from '../queries/use-contact-views';

type AccessType = 'USER' | 'TEAM';

interface RenameViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: ContactView | null;
  onRename: (name: string, access: AccessType) => void;
  isLoading?: boolean;
}

export function RenameViewDialog({
  open,
  onOpenChange,
  view,
  onRename,
  isLoading = false,
}: RenameViewDialogProps) {
  const { t } = useTranslation('desktop_ui');
  const [name, setName] = useState('');
  const [access, setAccess] = useState<AccessType>('USER');
  const { totalSeats } = useTeamSubscriptionData();
  const hasTeam = totalSeats > 0;

  useEffect(() => {
    if (open && view) {
      setName(view.name);
      setAccess(((view as any).owner_type as AccessType) ?? 'USER');
    } else if (!open) {
      setName('');
      setAccess('USER');
    }
  }, [open, view]);

  const hasChanges = name.trim() !== view?.name || access !== ((view as any)?.owner_type ?? 'USER');

  const handleRename = useCallback(() => {
    if (name.trim() && hasChanges) {
      onRename(name.trim(), access);
    }
  }, [name, access, onRename, hasChanges]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && name.trim() && !isLoading) {
        handleRename();
      }
    },
    [handleRename, name, isLoading],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[400px] overflow-hidden rounded-2xl p-0 gap-0"
        showCloseIcon={false}
      >
        <DialogHeader className="gap-1 px-4 py-3">
          <DialogTitle>
            <H4 className="text-primary-100">{t('components.contacts.views.dialog.editTitle')}</H4>
          </DialogTitle>
          <DialogDescription>
            <P3 className="text-primary-60">
              {t('components.contacts.views.dialog.editDescription')}
            </P3>
          </DialogDescription>
        </DialogHeader>
        <div className="px-4 pb-3">
          <label className="mb-3 block">
            <P3 className="mb-1 text-primary-60">
              {t('components.contacts.views.dialog.nameLabel')}
            </P3>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('components.contacts.views.dialog.namePlaceholder')}
              className="w-full rounded-md border border-primary-10 bg-white px-3 py-2 font-inter text-s text-primary-100 outline-none placeholder:text-primary-40 focus:border-primary-40"
            />
          </label>
          {hasTeam && (
            <div>
              <P3 className="mb-1 text-primary-60">
                {t('components.contacts.views.dialog.accessLabel')}
              </P3>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setAccess('USER')}
                  className={cn(
                    'flex h-8 flex-1 cursor-pointer items-center justify-center rounded-md border px-3 transition-colors',
                    access === 'USER'
                      ? 'border-highlight-default bg-highlight-default/10 text-highlight-default'
                      : 'border-primary-10 bg-white text-primary-60 hover:bg-primary-5',
                  )}
                >
                  <P3 className={access === 'USER' ? 'font-medium' : ''}>
                    {t('components.contacts.views.dialog.accessOnlyMe')}
                  </P3>
                </button>
                <button
                  type="button"
                  onClick={() => setAccess('TEAM')}
                  className={cn(
                    'flex h-8 flex-1 cursor-pointer items-center justify-center rounded-md border px-3 transition-colors',
                    access === 'TEAM'
                      ? 'border-highlight-default bg-highlight-default/10 text-highlight-default'
                      : 'border-primary-10 bg-white text-primary-60 hover:bg-primary-5',
                  )}
                >
                  <P3 className={access === 'TEAM' ? 'font-medium' : ''}>
                    {t('components.contacts.views.dialog.accessTeam')}
                  </P3>
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-primary-5 p-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            {t('components.contacts.views.dialog.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleRename}
            disabled={!name.trim() || !hasChanges || isLoading}
            isLoading={isLoading}
          >
            {t('components.contacts.views.dialog.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
